import { useCallback } from 'react';
import { Suggestion } from './types';

interface UseAutocompleteKeyboardProps {
  isAutocompleteOpen: boolean;
  suggestions: Suggestion[];
  focusedSuggestionIndex: number;
  setIsAutocompleteOpen: (open: boolean) => void;
  setFocusedSuggestionIndex: (index: number) => void;
  handleSelectSuggestion: (suggestion: Suggestion) => void;
}

export function useAutocompleteKeyboard({
  isAutocompleteOpen,
  suggestions,
  focusedSuggestionIndex,
  setIsAutocompleteOpen,
  setFocusedSuggestionIndex,
  handleSelectSuggestion,
}: UseAutocompleteKeyboardProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Ctrl+Space to open autocomplete
      if (e.ctrlKey && e.key === ' ') {
        e.preventDefault();
        setIsAutocompleteOpen(true);
        return;
      }

      // Handle Escape
      if (e.key === 'Escape') {
        if (isAutocompleteOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsAutocompleteOpen(false);
          return;
        }
        return;
      }

      if (!isAutocompleteOpen) {
        // Open on arrow down if we have suggestions
        if (e.key === 'ArrowDown' && suggestions.length > 0) {
          e.preventDefault();
          setIsAutocompleteOpen(true);
        }
        return;
      }

      // Navigation when open - skip hints
      const selectableIndices = suggestions
        .map((s, i) => (!s.isHint ? i : -1))
        .filter((i) => i !== -1);

      if (selectableIndices.length === 0) return; // No selectable items

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          const currentSelectableIndex = selectableIndices.indexOf(
            focusedSuggestionIndex
          );
          const nextSelectableIndex =
            currentSelectableIndex < selectableIndices.length - 1
              ? selectableIndices[currentSelectableIndex + 1]
              : selectableIndices[0];
          setFocusedSuggestionIndex(nextSelectableIndex);
          break;

        case 'ArrowUp':
          e.preventDefault();
          const currentSelectableIndexUp = selectableIndices.indexOf(
            focusedSuggestionIndex
          );
          const prevSelectableIndex =
            currentSelectableIndexUp > 0
              ? selectableIndices[currentSelectableIndexUp - 1]
              : selectableIndices[selectableIndices.length - 1];
          setFocusedSuggestionIndex(prevSelectableIndex);
          break;

        case 'Enter':
          e.preventDefault();
          const selectedSuggestion = suggestions[focusedSuggestionIndex];
          if (selectedSuggestion && !selectedSuggestion.isHint) {
            handleSelectSuggestion(selectedSuggestion);
          }
          break;

        case 'Tab':
          const tabSuggestion = suggestions[focusedSuggestionIndex];
          if (tabSuggestion && !tabSuggestion.isHint) {
            e.preventDefault();
            handleSelectSuggestion(tabSuggestion);
          }
          break;
      }
    },
    [
      isAutocompleteOpen,
      suggestions,
      focusedSuggestionIndex,
      handleSelectSuggestion,
      setIsAutocompleteOpen,
      setFocusedSuggestionIndex,
    ]
  );

  return { handleKeyDown };
}
